import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { 
  PersonAdd,
  Verified,
  Warning,
  Info,
  CheckCircle,
  Link as LinkIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [xmAccountNumber, setXmAccountNumber] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    // Create target date in UTC+2 (South African Standard Time)
    const targetDate = new Date('2025-12-31T23:59:59+02:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setXmAccountNumber(value);
    setVerificationStatus('');
  };

  const checkAffiliation = async () => {
    if (!xmAccountNumber.trim()) {
      setVerificationStatus('Please enter an account number');
      return;
    }

    setIsVerifying(true);
    setVerificationStatus('');

    try {
      const response = await fetch(
        `https://us-central1-fxwars.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.isAffiliated) {
          setVerificationStatus('✅ Account successfully verified and affiliated with T1 FX Team!');
        } else {
          setVerificationStatus('❌ Account not found or not affiliated with T1 FX Team. Please check your account number or contact support.');
        }
      } else {
        setVerificationStatus('⚠️ Verification failed. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error checking affiliation:', error);
      setVerificationStatus('⚠️ Network error. Please check your connection and try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ pt: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(/BearsBulls.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(245,247,250,0.8) 0%, rgba(195,207,226,0.8) 100%)',
            zIndex: 1,
          }}
        />
        
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            background: `
              radial-gradient(circle at 20% 50%, #1976d2 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #9c27b0 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, #1976d2 0%, transparent 50%)
            `,
            zIndex: 2,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 3 }}>
          {/* Top Affiliation Section */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                p: 2,
                mb: 3,
              }}
            >
              {/* Affiliation Notice */}
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8, fontSize: '0.85rem' }}>
                  💡 <strong>Note:</strong> Account affiliation with T1 FX Team is required to participate. Only Standard trading accounts are eligible.
                </Typography>
              </Box>
              
              {/* Broker Cards - Horizontal Layout */}
              <Grid container spacing={1.5} justifyContent="center" alignItems="stretch">
                {/* XM Section */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(25, 118, 210, 0.2)',
                      height: '100%',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                        <img 
                          src="/partnersLogo-15years.svg" 
                          alt="XM Global Logo" 
                          style={{ 
                            width: '60px', 
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.7rem' }}>
                        Sign up with affiliate link
                      </Typography>
                      <Button
                        href="https://affs.click/bwvBB"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem',
                          py: 0.2,
                          px: 0.8,
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          height: '22px',
                          mb: 0.75
                        }}
                        startIcon={<LinkIcon sx={{ fontSize: '10px !important' }} />}
                      >
                        Open Account
                      </Button>
                      <Box sx={{ 
                        backgroundColor: 'rgba(25, 118, 210, 0.1)', 
                        p: 0.3, 
                        borderRadius: 1, 
                        border: '1px dashed rgba(25, 118, 210, 0.3)'
                      }}>
                        <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.7rem' }}>
                          Code: T1FXTEAM
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Verification Section - Between Cards */}
                <Grid item xs={12} sm={12} md={2}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(25, 118, 210, 0.2)',
                      height: '100%',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1, sm: 1.25 }, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 1, 
                          fontSize: { xs: '0.75rem', sm: '0.7rem' }, 
                          lineHeight: 1.3 
                        }}
                      >
                        Already have an XM account? Verify your affiliation with T1 FX Team
                      </Typography>
                      
                      <Stack 
                        direction="column"
                        spacing={0.75}
                        alignItems="center"
                      >
                        <TextField
                          value={xmAccountNumber}
                          onChange={handleAccountNumberChange}
                          placeholder="Enter XM Account Number"
                          size="small"
                          sx={{ 
                            backgroundColor: 'white',
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              height: '30px',
                            }
                          }}
                          inputProps={{
                            style: { fontSize: '0.75rem' }
                          }}
                        />
                        
                        <Button
                          onClick={checkAffiliation}
                          disabled={isVerifying}
                          variant="contained"
                          sx={{ 
                            height: '30px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            px: 2,
                            width: '100%',
                            minWidth: 'auto'
                          }}
                        >
                          {isVerifying ? <CircularProgress size={12} color="inherit" /> : 'Verify'}
                        </Button>
                      </Stack>
                      
                      {verificationStatus && (
                        <Alert 
                          severity={verificationStatus.includes('✅') ? 'success' : verificationStatus.includes('❌') ? 'error' : 'warning'}
                          sx={{ 
                            mt: 1, 
                            fontSize: { xs: '0.65rem', sm: '0.6rem' },
                            py: 0.5,
                            textAlign: 'left'
                          }}
                        >
                          {verificationStatus}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Weltrade Section */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(156, 39, 176, 0.2)',
                      height: '100%',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                        <img 
                          src="/Weltradeblack_5b8221f8.png" 
                          alt="Weltrade Logo" 
                          style={{ 
                            width: '80px', 
                            height: 'auto',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.7rem' }}>
                        Sign up with affiliate link
                      </Typography>
                      <Button
                        href="https://track.gowt.me/visit/?bta=40955&brand=weltrade"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem',
                          py: 0.2,
                          px: 0.8,
                          borderColor: 'secondary.main',
                          color: 'secondary.main',
                          height: '22px',
                          mb: 0.75
                        }}
                        startIcon={<LinkIcon sx={{ fontSize: '10px !important' }} />}
                      >
                        Open Account
                      </Button>
                      <Box sx={{ 
                        backgroundColor: 'rgba(156, 39, 176, 0.1)', 
                        p: 0.3, 
                        borderRadius: 1, 
                        border: '1px dashed rgba(156, 39, 176, 0.3)'
                      }}>
                        <Typography variant="body2" fontWeight="bold" color="secondary.main" sx={{ fontSize: '0.7rem' }}>
                          Code: CX40955
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </motion.div>

          {/* Main Hero Content Below */}
          <Grid container spacing={4} alignItems="center">
            {/* Left side - Hero Text */}
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Stack spacing={3}>
                  <Chip
                    label="🔥 Live Competition Running"
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      color: 'secondary.main',
                      fontWeight: 600,
                      alignSelf: 'flex-start',
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'text.primary',
                      mb: 2,
                    }}
                  >
                    The Ultimate
                    <br />
                    <span style={{ color: '#1976d2' }}>Forex</span> Battle
                  </Typography>
                  
                  <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                    Compete with the world's best traders and win massive cash prizes. 
                    Test your skills in our yearly trading competition.
                  </Typography>
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      component={Link}
                      to="/register"
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: 'primary.main',
                        py: 2,
                        px: 4,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Join Competition
                    </Button>
                    
                    <Button
                      component={Link}
                      to="/rules"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        py: 2,
                        px: 4,
                        fontSize: '1.1rem',
                        '&:hover': {
                          borderColor: 'primary.light',
                          color: 'primary.light',
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        },
                      }}
                    >
                      Learn Rules
                    </Button>
                  </Stack>
                </Stack>
              </motion.div>
            </Grid>
            
            {/* Right side - Competition Card */}
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    background: 'transparent',
                    borderRadius: 0,
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="h4" gutterBottom color="primary.main">
                    Current Competition
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h2" color="secondary.main">
                        R1,000,000
                      </Typography>
                      <Typography color="text.secondary">Total Prize Pool</Typography>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="text.primary">
                          {timeLeft.days} Days
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Until End
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary" textAlign="center" gutterBottom>
                        Competition Ends In:
                      </Typography>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                          <Box textAlign="center">
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              {timeLeft.days}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Days
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box textAlign="center">
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              {timeLeft.hours}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Hours
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box textAlign="center">
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              {timeLeft.minutes}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Min
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box textAlign="center">
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              {timeLeft.seconds}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sec
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <Button
                      component={Link}
                      to="/register"
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: 'secondary.main',
                        '&:hover': {
                          backgroundColor: 'secondary.dark',
                        },
                      }}
                    >
                      Enter Now
                    </Button>
                  </Stack>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h3"
              gutterBottom
              sx={{ color: 'white', fontWeight: 'bold' }}
            >
              Ready to Start Trading?
            </Typography>
            
            <Typography
              variant="h6"
              sx={{ color: 'white', mb: 4, opacity: 0.9 }}
            >
              Join thousands of traders competing for massive prizes
            </Typography>
            
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Get Started Now
              </Button>
              
              <Button
                component={Link}
                to="/leaderboard"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                View Leaderboard
              </Button>
              
              <Button
                component={Link}
                to="/prizes"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                View Prizes
              </Button>
            </Stack>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 